import { io } from 'socket.io-client'
import { API_BASE_URL } from '../../utils/api'

// Video call API base URL
const VIDEO_API_BASE_URL = API_BASE_URL;

let socket = null

/**
 * Initialize Socket.io connection
 * @returns {Socket} Socket instance
 */
export function initSocket() {
  if (socket && socket.connected) {
    return socket
  }

  socket = io(VIDEO_API_BASE_URL, {
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionAttempts: 5,
  })

  socket.on('connect', () => {
    // Connected
  })

  socket.on('disconnect', () => {
    // Disconnected
  })

  socket.on('connect_error', (error) => {
    console.error('[Socket] Connection error:', error)
  })

  return socket
}

/**
 * Get existing socket instance
 * @returns {Socket|null} Socket instance or null
 */
export function getSocket() {
  return socket
}

/**
 * Disconnect socket
 */
export function disconnectSocket() {
  if (socket) {
    socket.disconnect()
    socket = null
  }
}

/**
 * Join a room
 * @param {string} roomId - Room ID
 */
export function joinRoom(roomId) {
  if (socket) {
    socket.emit('join-room', roomId)
  }
}

/**
 * Send WebRTC offer
 * @param {string} roomId - Room ID
 * @param {RTCSessionDescription} offer - WebRTC offer
 */
export function sendOffer(roomId, offer) {
  if (socket) {
    socket.emit('offer', { roomId, offer })
  }
}

/**
 * Send WebRTC answer
 * @param {string} to - Target socket ID
 * @param {RTCSessionDescription} answer - WebRTC answer
 */
export function sendAnswer(to, answer) {
  if (socket) {
    socket.emit('answer', { to, answer })
  }
}

/**
 * Send ICE candidate
 * @param {string} roomId - Room ID
 * @param {RTCIceCandidate} candidate - ICE candidate
 */
export function sendIceCandidate(roomId, candidate) {
  if (socket) {
    socket.emit('ice-candidate', { roomId, candidate })
  }
}

/**
 * Send audio state
 * @param {string} roomId - Room ID
 * @param {boolean} muted - Is muted
 */
export function sendAudioState(roomId, muted) {
  if (socket) {
    socket.emit('audio-state', { roomId, muted })
  }
}

/**
 * Start recording notification
 * @param {string} roomId - Room ID
 */
export function startRecording(roomId) {
  if (socket) {
    socket.emit('start-recording', { roomId })
  }
}

/**
 * Stop recording notification
 * @param {string} roomId - Room ID
 */
export function stopRecording(roomId) {
  if (socket) {
    socket.emit('stop-recording', { roomId })
  }
}

/**
 * Send transcript
 * @param {string} roomId - Room ID
 * @param {string} speaker - Speaker name
 * @param {string} text - Transcript text
 * @param {number} timestamp - Timestamp
 */
export function sendTranscript(roomId, speaker, text, timestamp) {
  if (socket) {
    socket.emit('transcript', { roomId, speaker, text, timestamp })
  }
}

/**
 * Send transcript status
 * @param {string} roomId - Room ID
 * @param {boolean} isOpen - Is transcript panel open
 * @param {string} participant - Participant name
 */
export function sendTranscriptStatus(roomId, isOpen, participant) {
  if (socket) {
    socket.emit('transcript-status', { roomId, isOpen, participant })
  }
}

export default {
  initSocket,
  getSocket,
  disconnectSocket,
  joinRoom,
  sendOffer,
  sendAnswer,
  sendIceCandidate,
  sendAudioState,
  startRecording,
  stopRecording,
  sendTranscript,
  sendTranscriptStatus,
}
